import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useToast } from "@chakra-ui/react";

export interface IFeedback {
  id: string;
  rating: number;
  comment?: string;
  mealServingId?: string;
  AdhocServingId?: string;
}

const key = "feedback";
const endpoint = "/api/feedback";

export const useFeedbackQuery = (props: DataManagerProps<IFeedback> = {}) =>
  useDataManager<IFeedback>(key, `${endpoint}/query`, props);
export const useFeedbacks = () => useEntities<IFeedback>(key, endpoint);
export const useFeedback = (
  id: string | undefined,
  showtoast: boolean = true,
) => {
  const base = useEntity<IFeedback>(
    key,
    endpoint,
    id,
    showtoast
      ? {
          add: "Feedback added",
          update: "Feedback updated",
          delete: "Feedback deleted",
        }
      : undefined,
  );

  const queryClient = useQueryClient();

  const invalidateActive = () => {
    queryClient.invalidateQueries({
      queryKey: [key],
      type: "active",
    });
  };

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  const bulkAdd = useMutation({
    mutationFn: async (feedbacks: Omit<IFeedback, "id">[]) => {
      const response = await axios.post(`${endpoint}/bulk`, feedbacks);
      return response.data;
    },
    onMutate: () => {
      toast.close(`bulk-add-toast-${key}`);
    },
    onSuccess: (data) => {
      if (!toast.isActive(`bulk-add-toast-${key}`)) {
        toast({
          title: "Feedback added",
          status: "success",
          id: `bulk-add-toast-${key}`,
        });
      }

      queryClient.setQueryData([key], data);
      invalidateActive();
    },
    onError: (error: any) => {
      if (!toast.isActive(`bulk-add-error-toast-${key}`)) {
        toast({
          title: error?.response?.data ?? "Bulk feedback failed",
          status: "error",
          id: `bulk-add-error-toast-${key}`,
        });
      }
    },
  });
  return { ...base, bulkAdd };
};
